import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileBarChart, Building2, Cpu, Package, Trash2, Download, FileSpreadsheet, FileText, Calendar, Store } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const [cityStats, setCityStats] = useState([]);
  const [statistics, setStatistics] = useState({
    total_hospitals: 0,
    total_devices: 0,
    total_probes: 0,
    cities_count: 0,
    active_devices: 0,
  });
  const [devices, setDevices] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [probeDeliveries, setProbeDeliveries] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [cityDealerMap, setCityDealerMap] = useState({});
  const [selectedDealer, setSelectedDealer] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [invoiceDate, setInvoiceDate] = useState("");
  
  // Date range filter states
  const [filterMode, setFilterMode] = useState("month"); // "month" or "range"
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchData = async () => {
    try {
      const [statsRes, cityRes, devicesRes, hospitalsRes, probeRes, dealersRes, cityMapRes] = await Promise.all([
        axios.get(`${API}/statistics`),
        axios.get(`${API}/statistics/by-city`),
        axios.get(`${API}/devices`),
        axios.get(`${API}/hospitals`),
        axios.get(`${API}/probe-deliveries`),
        axios.get(`${API}/dealers`),
        axios.get(`${API}/city-dealer-map`),
      ]);

      setStatistics(statsRes.data);
      setCityStats(cityRes.data);
      setDevices(devicesRes.data);
      setHospitals(hospitalsRes.data);
      setProbeDeliveries(probeRes.data);
      setDealers(dealersRes.data);
      setCityDealerMap(cityMapRes.data);
    } catch (error) {
      console.error("Error fetching reports data:", error);
      toast.error("Raporlar yüklenirken hata oluştu");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Set default to current month/year
    const now = new Date();
    setSelectedYear(now.getFullYear().toString());
    setSelectedMonth((now.getMonth() + 1).toString());
  }, []);

  const getHospitalName = (hospitalId) => {
    const hospital = hospitals.find((h) => h.id === hospitalId);
    return hospital ? hospital.name : "-";
  };

  const getHospitalCity = (hospitalId) => {
    const hospital = hospitals.find((h) => h.id === hospitalId);
    return hospital ? hospital.city : "-";
  };

  // Dealer-based filtering
  const getDealerCities = (dealerId) => {
    if (dealerId === "all") return null;
    const dealer = dealers.find(d => d.id === dealerId);
    return dealer?.assigned_cities || [];
  };

  const dealerCities = getDealerCities(selectedDealer);

  const filteredHospitals = dealerCities
    ? hospitals.filter(h => dealerCities.includes(h.city))
    : hospitals;

  const filteredHospitalIds = new Set(filteredHospitals.map(h => h.id));

  const filteredDevices = dealerCities
    ? devices.filter(d => filteredHospitalIds.has(d.hospital_id))
    : devices;

  const filteredCityStats = dealerCities
    ? cityStats.filter(c => dealerCities.includes(c.city))
    : cityStats;

  const dealerFilteredProbes = dealerCities
    ? probeDeliveries.filter(d => filteredHospitalIds.has(d.hospital_id))
    : probeDeliveries;

  // Dealer summary stats
  const dealerStats = {
    hospitals: filteredHospitals.length,
    devices: filteredDevices.length,
    totalProbes: dealerFilteredProbes.reduce((sum, d) => sum + d.quantity, 0),
    cities: dealerCities ? dealerCities.length : statistics.cities_count,
  };

  const handleInvoiceToggle = async (delivery) => {
    if (delivery.invoice_issued) {
      // If already invoiced, cancel it
      try {
        await axios.put(`${API}/probe-deliveries/${delivery.id}/invoice`, {
          invoice_issued: false,
          invoice_date: null,
          invoice_amount: null,
        });
        toast.success("Fatura durumu iptal edildi");
        fetchData();
      } catch (error) {
        console.error("Error updating invoice status:", error);
        toast.error("Fatura durumu güncellenirken hata oluştu");
      }
    } else {
      // Open dialog to enter invoice amount and date
      setSelectedDelivery(delivery);
      const calculatedAmount = delivery.quantity * (delivery.unit_price || 0);
      setInvoiceAmount(calculatedAmount);
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      setIsInvoiceDialogOpen(true);
    }
  };

  const handleInvoiceSubmit = async () => {
    if (!selectedDelivery) return;

    try {
      await axios.put(`${API}/probe-deliveries/${selectedDelivery.id}/invoice`, {
        invoice_issued: true,
        invoice_date: invoiceDate,
        invoice_amount: invoiceAmount,
      });
      toast.success("Fatura kesildi olarak işaretlendi");
      setIsInvoiceDialogOpen(false);
      setSelectedDelivery(null);
      setInvoiceAmount(0);
      setInvoiceDate("");
      fetchData();
    } catch (error) {
      console.error("Error updating invoice status:", error);
      toast.error("Fatura durumu güncellenirken hata oluştu");
    }
  };

  const handleDeleteDelivery = async (deliveryId, deliveryInfo) => {
    if (!window.confirm(`${deliveryInfo.hospitalName} için ${deliveryInfo.probeType} teslimatını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/probe-deliveries/${deliveryId}`);
      toast.success("Prob teslimatı silindi");
      fetchData();
    } catch (error) {
      console.error("Error deleting delivery:", error);
      toast.error("Prob teslimatı silinirken hata oluştu");
    }
  };

  const uninvoicedProbes = dealerFilteredProbes.filter(d => !d.invoice_issued);
  const uninvoicedTotal = uninvoicedProbes.reduce((sum, d) => sum + d.quantity, 0);

  // Filter deliveries by selected month/year OR date range + dealer
  const filteredDeliveries = dealerFilteredProbes.filter(delivery => {
    if (filterMode === "range" && startDate && endDate) {
      const deliveryDate = new Date(delivery.delivery_date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include the end date fully
      return deliveryDate >= start && deliveryDate <= end;
    }
    
    // Month/Year filter mode
    if (!selectedMonth || selectedMonth === "all" || !selectedYear || selectedYear === "all") {
      if (selectedYear && selectedYear !== "all") {
        const deliveryYear = new Date(delivery.delivery_date).getFullYear();
        return deliveryYear === parseInt(selectedYear);
      }
      if (selectedMonth && selectedMonth !== "all") {
        const deliveryMonth = new Date(delivery.delivery_date).getMonth() + 1;
        return deliveryMonth === parseInt(selectedMonth);
      }
      return true;
    }
    
    const deliveryDate = new Date(delivery.delivery_date);
    const deliveryMonth = deliveryDate.getMonth() + 1;
    const deliveryYear = deliveryDate.getFullYear();
    
    return deliveryMonth === parseInt(selectedMonth) && deliveryYear === parseInt(selectedYear);
  });

  // Export to Excel function
  const exportToExcel = () => {
    if (filteredDeliveries.length === 0) {
      toast.error("Dışa aktarılacak veri bulunamadı");
      return;
    }

    const exportData = filteredDeliveries.map(delivery => ({
      "Hastane": getHospitalName(delivery.hospital_id),
      "Şehir": getHospitalCity(delivery.hospital_id),
      "Prob Tipi": delivery.probe_type,
      "Adet": delivery.quantity,
      "Birim Fiyat (₺)": delivery.unit_price || 0,
      "Toplam (₺)": delivery.quantity * (delivery.unit_price || 0),
      "Teslim Tarihi": new Date(delivery.delivery_date).toLocaleDateString('tr-TR'),
      "Bayi": delivery.dealer_name || "-",
      "Fatura Durumu": delivery.invoice_issued ? "Kesildi" : "Bekliyor",
      "Fatura Tarihi": delivery.invoice_date ? new Date(delivery.invoice_date).toLocaleDateString('tr-TR') : "-",
      "Fatura Tutarı (₺)": delivery.invoice_amount || "-"
    }));

    // Add summary row
    exportData.push({});
    exportData.push({
      "Hastane": "ÖZET",
      "Şehir": "",
      "Prob Tipi": "",
      "Adet": filteredDeliveries.reduce((sum, d) => sum + d.quantity, 0),
      "Birim Fiyat (₺)": "",
      "Toplam (₺)": totalAmount,
      "Teslim Tarihi": "",
      "Fatura Durumu": `Kesilen: ${monthInvoiced.length}, Bekleyen: ${monthUninvoiced.length}`,
      "Fatura Tarihi": "",
      "Fatura Tutarı (₺)": invoicedAmount
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Prob Fatura Raporu");
    
    // Generate filename with date
    const dateStr = filterMode === "range" && startDate && endDate
      ? `${startDate}_${endDate}`
      : `${selectedYear || "tum"}_${selectedMonth || "tum"}`;
    
    XLSX.writeFile(wb, `Prob_Fatura_Raporu_${dateStr}.xlsx`);
    toast.success("Excel dosyası indirildi");
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (filteredDeliveries.length === 0) {
      toast.error("Dışa aktarılacak veri bulunamadı");
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    
    // Title
    doc.setFontSize(18);
    doc.text("Prob Fatura Raporu", 14, 22);
    
    // Date range info
    doc.setFontSize(10);
    let dateInfo = "";
    if (filterMode === "range" && startDate && endDate) {
      dateInfo = `Tarih Aralığı: ${new Date(startDate).toLocaleDateString('tr-TR')} - ${new Date(endDate).toLocaleDateString('tr-TR')}`;
    } else if (selectedYear && selectedMonth) {
      const monthName = months.find(m => m.value === selectedMonth)?.label || "";
      dateInfo = `Dönem: ${monthName} ${selectedYear}`;
    }
    doc.text(dateInfo, 14, 30);
    doc.text(`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`, 14, 36);
    
    // Summary
    doc.setFontSize(11);
    doc.text(`Toplam Tutar: ${totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL`, 14, 44);
    doc.text(`Kesilen Fatura: ${invoicedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL (${monthInvoiced.length} teslimat)`, 14, 50);
    doc.text(`Bekleyen Fatura: ${uninvoicedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL (${monthUninvoiced.length} teslimat)`, 14, 56);

    // Table data
    const tableData = filteredDeliveries.map(delivery => [
      getHospitalName(delivery.hospital_id),
      getHospitalCity(delivery.hospital_id),
      delivery.probe_type,
      delivery.quantity.toString(),
      (delivery.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
      (delivery.quantity * (delivery.unit_price || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
      new Date(delivery.delivery_date).toLocaleDateString('tr-TR'),
      delivery.dealer_name || "-",
      delivery.invoice_issued ? "Kesildi" : "Bekliyor",
      delivery.invoice_amount ? delivery.invoice_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : "-"
    ]);

    doc.autoTable({
      startY: 62,
      head: [["Hastane", "Şehir", "Prob Tipi", "Adet", "Birim Fiyat", "Toplam", "Teslim Tarihi", "Bayi", "Fatura", "Fatura Tutarı"]],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 22 },
        2: { cellWidth: 25 },
        3: { cellWidth: 12, halign: 'center' },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 25 },
        7: { cellWidth: 25 },
        8: { cellWidth: 18, halign: 'center' },
        9: { cellWidth: 22, halign: 'right' }
      }
    });

    // Generate filename with date
    const dateStr = filterMode === "range" && startDate && endDate
      ? `${startDate}_${endDate}`
      : `${selectedYear || "tum"}_${selectedMonth || "tum"}`;
    
    doc.save(`Prob_Fatura_Raporu_${dateStr}.pdf`);
    toast.success("PDF dosyası indirildi");
  };

  // Calculate totals for filtered month
  const monthInvoiced = filteredDeliveries.filter(d => d.invoice_issued);
  const monthUninvoiced = filteredDeliveries.filter(d => !d.invoice_issued);
  
  // Use invoice_amount if available, otherwise use calculated amount
  const invoicedAmount = monthInvoiced.reduce((sum, d) => {
    const amount = d.invoice_amount !== null && d.invoice_amount !== undefined 
      ? d.invoice_amount 
      : (d.quantity * (d.unit_price || 0));
    return sum + amount;
  }, 0);
  
  const uninvoicedAmount = monthUninvoiced.reduce((sum, d) => sum + (d.quantity * (d.unit_price || 0)), 0);
  const totalAmount = invoicedAmount + uninvoicedAmount;

  // Get available years and months from deliveries
  const years = [...new Set(probeDeliveries.map(d => new Date(d.delivery_date).getFullYear()))].sort((a, b) => b - a);
  const months = [
    { value: "1", label: "Ocak" },
    { value: "2", label: "Şubat" },
    { value: "3", label: "Mart" },
    { value: "4", label: "Nisan" },
    { value: "5", label: "Mayıs" },
    { value: "6", label: "Haziran" },
    { value: "7", label: "Temmuz" },
    { value: "8", label: "Ağustos" },
    { value: "9", label: "Eylül" },
    { value: "10", label: "Ekim" },
    { value: "11", label: "Kasım" },
    { value: "12", label: "Aralık" },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="reports-title">
          Prob Raporu
        </h1>
        <p className="text-base text-muted-foreground mt-2">
          Prob istatistikleri ve detaylı raporlar
        </p>
      </div>

      {/* Dealer Selector */}
      <Card data-testid="dealer-selector-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5 text-blue-600" />
              <Label className="font-semibold text-sm whitespace-nowrap">Bayi Seçin:</Label>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedDealer === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedDealer("all")}
                data-testid="dealer-filter-all"
              >
                Tümü
              </Button>
              {dealers.filter(d => d.is_active).map(dealer => (
                <Button
                  key={dealer.id}
                  variant={selectedDealer === dealer.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedDealer(dealer.id)}
                  data-testid={`dealer-filter-${dealer.id}`}
                  style={selectedDealer === dealer.id ? { backgroundColor: dealer.color, borderColor: dealer.color } : { borderColor: dealer.color + '60', color: dealer.color }}
                >
                  <span className="w-3 h-3 rounded-full mr-2 flex-shrink-0" style={{ backgroundColor: dealer.color }} />
                  {dealer.name}
                  <span className="ml-1 text-xs opacity-75">({dealer.assigned_cities?.length || 0} il)</span>
                </Button>
              ))}
            </div>
          </div>
          {selectedDealer !== "all" && (
            <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-700">{dealerStats.cities}</p>
                  <p className="text-xs text-blue-600">Şehir</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{dealerStats.hospitals}</p>
                  <p className="text-xs text-blue-600">Hastane</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{dealerStats.devices}</p>
                  <p className="text-xs text-blue-600">Cihaz</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{dealerStats.totalProbes}</p>
                  <p className="text-xs text-purple-600">Toplam Prob</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card data-testid="report-total-hospitals">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Toplam Hastane</p>
                <p className="text-3xl font-bold mt-2">{dealerStats.hospitals}</p>
              </div>
              <div className="bg-blue-50 text-blue-600 p-3 rounded-xl">
                <Building2 className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="report-total-devices">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Toplam Cihaz</p>
                <p className="text-3xl font-bold mt-2">{dealerStats.devices}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {statistics.active_devices} aktif
                </p>
              </div>
              <div className="bg-green-50 text-green-600 p-3 rounded-xl">
                <Cpu className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="report-total-probes">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Toplam Prob</p>
                <p className="text-3xl font-bold mt-2">{dealerStats.totalProbes}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Shuliang Ligasure
                </p>
              </div>
              <div className="bg-purple-50 text-purple-600 p-3 rounded-xl">
                <Package className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="report-cities-count">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Kapsanan Şehir</p>
                <p className="text-3xl font-bold mt-2">{dealerStats.cities}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  / 81 şehir
                </p>
              </div>
              <div className="bg-indigo-50 text-indigo-600 p-3 rounded-xl">
                <FileBarChart className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* City-based Report */}
      <Card data-testid="city-based-report">
        <CardHeader>
          <CardTitle>Şehir Bazlı Rapor</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Şehir</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Hastane Sayısı</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Cihaz Sayısı</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Prob Sayısı</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Ortalama Prob/Cihaz</th>
                </tr>
              </thead>
              <tbody>
                {filteredCityStats.map((city) => (
                  <tr key={city.city} className="border-b border-border table-row" data-testid={`city-report-${city.city}`}>
                    <td className="py-3 px-4 font-medium">{city.city}</td>
                    <td className="py-3 px-4 text-center">{city.hospital_count}</td>
                    <td className="py-3 px-4 text-center">{city.device_count}</td>
                    <td className="py-3 px-4 text-center font-semibold text-purple-600">
                      {city.probe_count}
                    </td>
                    <td className="py-3 px-4 text-center text-muted-foreground">
                      {city.device_count > 0 ? (city.probe_count / city.device_count).toFixed(1) : "0"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredCityStats.length === 0 && (
              <div className="text-center py-12" data-testid="no-city-stats">
                <p className="text-muted-foreground">Veri bulunamadı</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Device Inventory Report */}
      <Card data-testid="device-inventory-report">
        <CardHeader>
          <CardTitle>Cihaz ve Prob Envanter Raporu</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Cihaz Adı</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Seri No</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Hastane</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Şehir</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Durum</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Kurulum Tarihi</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map((device) => (
                  <tr key={device.id} className="border-b border-border table-row" data-testid={`device-inventory-${device.id}`}>
                    <td className="py-3 px-4 font-medium">{device.device_name}</td>
                    <td className="py-3 px-4 font-mono text-sm">{device.device_serial}</td>
                    <td className="py-3 px-4">{getHospitalName(device.hospital_id)}</td>
                    <td className="py-3 px-4 text-muted-foreground">{getHospitalCity(device.hospital_id)}</td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          device.status === "active"
                            ? "bg-green-100 text-green-700"
                            : device.status === "maintenance"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {device.status === "active" ? "Aktif" : device.status === "maintenance" ? "Bakımda" : "Pasif"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{device.installation_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredDevices.length === 0 && (
              <div className="text-center py-12" data-testid="no-device-inventory">
                <p className="text-muted-foreground">Veri bulunamadı</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Probe Invoice Report */}
      <Card data-testid="probe-invoice-report">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle>Prob Fatura Raporu</CardTitle>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Filter Mode Toggle */}
              <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
                <Button
                  variant={filterMode === "month" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFilterMode("month")}
                  data-testid="filter-mode-month"
                >
                  Ay/Yıl
                </Button>
                <Button
                  variant={filterMode === "range" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFilterMode("range")}
                  data-testid="filter-mode-range"
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Tarih Aralığı
                </Button>
              </div>

              {filterMode === "month" ? (
                <>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="w-32" data-testid="year-filter">
                      <SelectValue placeholder="Yıl" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Yıllar</SelectItem>
                      {years.map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-32" data-testid="month-filter">
                      <SelectValue placeholder="Ay" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Aylar</SelectItem>
                      {months.map(month => (
                        <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-40"
                      data-testid="start-date-filter"
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-40"
                      data-testid="end-date-filter"
                    />
                  </div>
                </>
              )}

              {/* Export Buttons */}
              <div className="flex items-center gap-2 ml-2 border-l pl-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToExcel}
                  data-testid="export-excel-btn"
                  className="text-green-600 border-green-300 hover:bg-green-50"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-1" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToPDF}
                  data-testid="export-pdf-btn"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600 font-medium">Toplam Tutar</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">
                  {totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {filteredDeliveries.length} teslimat
                </p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <p className="text-sm text-green-600 font-medium">Kesilen Fatura</p>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  {invoicedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {monthInvoiced.length} teslimat
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <p className="text-sm text-red-600 font-medium">Bekleyen Fatura</p>
                <p className="text-2xl font-bold text-red-700 mt-1">
                  {uninvoicedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {monthUninvoiced.length} teslimat
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Hastane</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Şehir</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Prob Tipi</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Adet</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Birim Fiyat</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Toplam</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Teslim Tarihi</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Bayi</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Fatura Durumu</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeliveries
                  .sort((a, b) => {
                    // Sort by invoice status first (uninvoiced first), then by date
                    if (a.invoice_issued !== b.invoice_issued) {
                      return a.invoice_issued ? 1 : -1;
                    }
                    return new Date(b.delivery_date) - new Date(a.delivery_date);
                  })
                  .map((delivery) => {
                    const totalPrice = delivery.quantity * (delivery.unit_price || 0);
                    return (
                      <tr 
                        key={delivery.id} 
                        className={`border-b border-border table-row ${!delivery.invoice_issued ? 'bg-red-50' : ''}`}
                        data-testid={`probe-invoice-${delivery.id}`}
                      >
                        <td className="py-3 px-4 font-medium">{getHospitalName(delivery.hospital_id)}</td>
                        <td className="py-3 px-4 text-muted-foreground">{getHospitalCity(delivery.hospital_id)}</td>
                        <td className="py-3 px-4">{delivery.probe_type}</td>
                        <td className="py-3 px-4 text-center font-semibold text-purple-600">
                          {delivery.quantity}
                        </td>
                        <td className="py-3 px-4 text-right text-muted-foreground">
                          {(delivery.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">
                          {totalPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {new Date(delivery.delivery_date).toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="py-3 px-4">
                          {delivery.dealer_name ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                              {delivery.dealer_name}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {delivery.invoice_issued ? (
                            <div className="flex flex-col items-center">
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                                ✓ Fatura Kesildi
                              </span>
                              {delivery.invoice_date && (
                                <span className="text-xs text-muted-foreground mt-1">
                                  {new Date(delivery.invoice_date).toLocaleDateString('tr-TR')}
                                </span>
                              )}
                              {delivery.invoice_amount !== null && delivery.invoice_amount !== undefined && (
                                <span className="text-xs font-bold text-green-700 mt-1">
                                  {delivery.invoice_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">
                              ⚠ Bekliyor
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant={delivery.invoice_issued ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleInvoiceToggle(delivery)}
                              data-testid={`toggle-invoice-${delivery.id}`}
                              className={delivery.invoice_issued ? "" : "bg-green-600 hover:bg-green-700 text-white"}
                            >
                              {delivery.invoice_issued ? "İptal Et" : "Fatura Kesildi"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDelivery(delivery.id, {
                                hospitalName: getHospitalName(delivery.hospital_id),
                                probeType: delivery.probe_type
                              })}
                              data-testid={`delete-delivery-${delivery.id}`}
                              className="text-destructive hover:text-destructive hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {filteredDeliveries.length === 0 && (
              <div className="text-center py-12" data-testid="no-probe-invoices">
                <p className="text-muted-foreground">
                  {selectedMonth && selectedYear 
                    ? "Seçilen ay için prob teslimatı bulunmamaktadır" 
                    : "Prob teslimatı bulunmamaktadır"}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Dialog */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent className="max-w-md" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>Fatura Bilgileri</DialogTitle>
          </DialogHeader>
          
          {selectedDelivery && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary rounded-lg">
                <p className="text-sm font-medium text-muted-foreground">Hastane</p>
                <p className="font-semibold">{getHospitalName(selectedDelivery.hospital_id)}</p>
                <p className="text-sm text-muted-foreground mt-2">Prob Tipi</p>
                <p className="font-medium">{selectedDelivery.probe_type}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Adet</Label>
                  <Input value={selectedDelivery.quantity} disabled className="font-semibold" />
                </div>
                <div>
                  <Label>Birim Fiyat</Label>
                  <Input 
                    value={(selectedDelivery.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺'} 
                    disabled 
                    className="font-semibold" 
                  />
                </div>
              </div>

              <div>
                <Label>Otomatik Hesaplanan Tutar</Label>
                <Input 
                  value={(selectedDelivery.quantity * (selectedDelivery.unit_price || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺'}
                  disabled
                  className="font-bold text-blue-600"
                />
              </div>

              <div className="border-t pt-4">
                <Label htmlFor="invoice_date">Fatura Edilme Tarihi *</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  data-testid="invoice-date-input"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="mt-2"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Bu tarih, faturanın hangi ayın KDV'sine dahil olacağını belirler
                </p>
              </div>

              <div>
                <Label htmlFor="invoice_amount">Hak Ediş Miktarı (₺) *</Label>
                <Input
                  id="invoice_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  data-testid="invoice-amount-input"
                  placeholder="0.00"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(parseFloat(e.target.value) || 0)}
                  className="mt-2 font-bold text-lg"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Faturalanacak tutarı giriniz
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsInvoiceDialogOpen(false);
                    setSelectedDelivery(null);
                    setInvoiceAmount(0);
                    setInvoiceDate("");
                  }}
                  data-testid="cancel-invoice-btn"
                >
                  İptal
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleInvoiceSubmit}
                  data-testid="confirm-invoice-btn"
                >
                  Fatura Kes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;